import api from './api';
import { ICal, ICalResponse, ICalRunResponse } from './types';

/**
 * ICal Service
 * Handles all iCal-related API requests
 */
const icalService = {
    /**
     * Get all iCals
     * @returns Promise<ICal[]>
     */
    getAll: async (): Promise<ICal[]> => {
        try {
            const response = await api.get<ICalResponse>('/icals');
            return response.icals || [];
        } catch (error) {
            console.error('Error fetching all icals:', error);
            throw error;
        }
    },

    /**
     * Get iCal by public ID
     * @param publicId - ICal public ID
     * @returns Promise<ICal>
     */
    getById: async (publicId: string): Promise<ICal> => {
        try {
            const response = await api.get<ICal>(`/icals/${publicId}`);
            return response;
        } catch (error) {
            console.error(`Error fetching ical ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Get iCals by house ID
     * @param houseId - House ID
     * @returns Promise<ICal[]>
     */
    getByHouseId: async (houseId: number): Promise<ICal[]> => {
        try {
            const response = await api.get<ICalResponse>(`/icals/house/${houseId}`);
            return response.icals || [];
        } catch (error) {
            console.error(`Error fetching icals for house ${houseId}:`, error);
            throw error;
        }
    },

    /**
     * Create new iCal
     * @param icalData - ICal data
     * @returns Promise<ICal>
     */
    create: async (icalData: Partial<ICal>): Promise<ICal> => {
        try {
            const response = await api.post<ICal>('/icals', icalData);
            return response;
        } catch (error) {
            console.error('Error creating ical:', error);
            throw error;
        }
    },

    /**
     * Update iCal
     * @param publicId - ICal public ID
     * @param icalData - ICal data to update
     * @returns Promise<ICal>
     */
    update: async (publicId: string, icalData: Partial<ICal>): Promise<ICal> => {
        try {
            const response = await api.put<ICal>(`/icals/${publicId}`, icalData);
            return response;
        } catch (error) {
            console.error(`Error updating ical ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Delete iCal
     * @param publicId - ICal public ID
     * @returns Promise<void>
     */
    delete: async (publicId: string): Promise<void> => {
        try {
            await api.delete(`/icals/${publicId}`);
        } catch (error) {
            console.error(`Error deleting ical ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Toggle iCal active status
     * @param publicId - ICal public ID
     * @param active - New active status
     * @returns Promise<void>
     */
    toggleActive: async (publicId: string, active: boolean): Promise<void> => {
        try {
            await api.patch(`/icals/${publicId}/toggle`, { active });
        } catch (error) {
            console.error(`Error toggling ical ${publicId} active status:`, error);
            throw error;
        }
    },

    /**
     * Run iCal (fetch data from URL)
     * @param publicId - ICal public ID
     * @returns Promise<ICalRunResponse>
     */
    run: async (publicId: string): Promise<ICalRunResponse> => {
        try {
            const response = await api.post<ICalRunResponse>(`/icals/${publicId}/run`);
            return response;
        } catch (error) {
            console.error(`Error running ical ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Run all active iCals
     * @returns Promise<{ results: Record<string, string>, total: number }>
     */
    runAll: async (): Promise<{ results: Record<string, string>, total: number }> => {
        try {
            const response = await api.post<{ results: Record<string, string>, total: number, success: boolean }>('/icals/run-all');
            return { results: response.results || {}, total: response.total || 0 };
        } catch (error) {
            console.error('Error running all icals:', error);
            throw error;
        }
    },
};

export default icalService;
